import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStaffAuth } from '../../stores/staffAuth';
import { apiStaff } from '../../api/staffClient';
import TopBar from '../../components/TopBar';

type ReclamoItem = {
  id: number;
  titulo: string;
  estado: string;
  prioridad: string;
  creado_en: string;
  ciudadano: string;
  categoria_icono: string | null;
  area: string | null;
};

const ESTADO_COLOR: Record<string, string> = {
  pendiente:  '#f59e0b',
  en_proceso: '#3b82f6',
  resuelto:   '#10b981',
  cerrado:    '#6b7280',
  rechazado:  '#ef4444',
};
const ESTADO_LABEL: Record<string, string> = {
  pendiente:  'Pendiente',
  en_proceso: 'En proceso',
  resuelto:   'Resuelto',
  cerrado:    'Cerrado',
  rechazado:  'Rechazado',
};
const ROL_LABEL: Record<string, string> = {
  superadmin:       'Super Admin',
  admin:            'Administrador',
  intendente:       'Intendente',
  secretario:       'Secretario',
  subsecretario:    'Subsecretario',
  director:         'Director',
  jefe_area:        'Jefe de Área',
  operador:         'Operador',
  jefe_seguridad:   'Jefe de Seguridad',
  agente_seguridad: 'Agente de Seguridad',
  radio_operador:   'Radio Operador',
};

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso.replace(' ', 'T')).getTime()) / 1000);
  if (diff < 60)    return 'ahora';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(iso.replace(' ', 'T')).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
}

export default function StaffHome() {
  const nav    = useNavigate();
  const staff  = useStaffAuth((s) => s.staff);
  const logout = useStaffAuth((s) => s.logout);

  const [recientes,  setRecientes]  = useState<ReclamoItem[]>([]);
  const [pendientes, setPendientes] = useState<number | null>(null);
  const [enProceso,  setEnProceso]  = useState<number | null>(null);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    Promise.all([
      apiStaff<{ items: ReclamoItem[]; total: number }>('/staff?action=reclamos&page=1'),
      apiStaff<{ total: number }>('/staff?action=reclamos&page=1&estado=pendiente'),
      apiStaff<{ total: number }>('/staff?action=reclamos&page=1&estado=en_proceso'),
    ])
      .then(([all, pend, proc]) => {
        setRecientes(all.items.slice(0, 5));
        setPendientes(pend.total);
        setEnProceso(proc.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function handleLogout() {
    logout();
    nav('/login/staff');
  }

  const rolLabel = ROL_LABEL[staff?.rol ?? ''] ?? staff?.rol ?? '';

  const ROLES_NECESITAN_AREA = ['operador', 'director', 'jefe_area', 'jefe_seguridad', 'agente_seguridad', 'radio_operador', 'cajero'];
  const sinArea = ROLES_NECESITAN_AREA.includes(staff?.rol ?? '') && !staff?.area_nombre;

  return (
    <>
      <TopBar
        title="Panel municipal"
        right={
          <button
            onClick={handleLogout}
            className="text-xs font-semibold px-3 py-1.5 rounded-xl flex-shrink-0"
            style={{ backgroundColor: 'rgba(100,116,139,0.13)', border: '1px solid rgba(100,116,139,0.22)', color: '#64748b' }}
          >
            Salir
          </button>
        }
      />

      <main className="max-w-md mx-auto px-4 py-4 pb-8 space-y-4">

        {/* Tarjeta de perfil */}
        <div className="bg-white border border-app rounded-2xl p-4 flex items-center gap-4">
          <img
            src={`${import.meta.env.BASE_URL}icon.png`}
            alt="CiudadApp"
            className="w-14 h-14 rounded-2xl flex-shrink-0 shadow-sm"
          />
          <div className="flex-1 min-w-0">
            <p className="font-extrabold text-gray-900 truncate text-base">
              {staff?.nombre} {staff?.apellido}
            </p>
            <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full mt-1 bg-brand-50 text-brand-600">
              {rolLabel}
            </span>
            {staff?.area_nombre && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">{staff.area_nombre}</p>
            )}
            {staff?.municipio_nombre && (
              <p className="text-xs text-gray-400 truncate">{staff.municipio_nombre}</p>
            )}
          </div>
        </div>

        {/* Aviso: sin área asignada */}
        {sinArea && (
          <div className="rounded-2xl px-4 py-3 flex items-start gap-3"
            style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
            <span className="text-lg leading-none mt-0.5">⚠️</span>
            <div>
              <p className="text-sm font-semibold" style={{ color: '#c2410c' }}>Sin área asignada</p>
              <p className="text-xs mt-0.5" style={{ color: '#9a3412' }}>
                Tu usuario no tiene un área configurada. No podrás ver reclamos hasta que un administrador te asigne un área.
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => nav('/staff/reclamos?estado=pendiente')}
            className="bg-white border border-app rounded-2xl p-4 text-left active:bg-gray-50 transition-colors"
          >
            <p className="text-3xl font-extrabold" style={{ color: '#f59e0b' }}>
              {pendientes === null ? '–' : pendientes}
            </p>
            <p className="text-xs text-gray-500 mt-1 font-semibold">Pendientes</p>
          </button>
          <button
            onClick={() => nav('/staff/reclamos?estado=en_proceso')}
            className="bg-white border border-app rounded-2xl p-4 text-left active:bg-gray-50 transition-colors"
          >
            <p className="text-3xl font-extrabold" style={{ color: '#3b82f6' }}>
              {enProceso === null ? '–' : enProceso}
            </p>
            <p className="text-xs text-gray-500 mt-1 font-semibold">En proceso</p>
          </button>
        </div>

        {/* Reclamos recientes */}
        <div className="bg-white border border-app rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="font-bold text-sm text-gray-800">Reclamos recientes</p>
            <button
              onClick={() => nav('/staff/reclamos')}
              className="text-xs font-semibold text-brand-600"
            >
              Ver todos →
            </button>
          </div>

          {loading ? (
            <p className="text-center text-gray-400 text-sm py-8">Cargando…</p>
          ) : recientes.length === 0 ? (
            <div className="text-center py-10 px-4">
              <span className="text-3xl">📋</span>
              <p className="mt-2 text-gray-400 text-sm">
                {staff?.area_nombre
                  ? `Sin reclamos en el área "${staff.area_nombre}"`
                  : 'Sin reclamos asignados'}
              </p>
            </div>
          ) : (
            <ul>
              {recientes.map((r, i) => (
                <li key={r.id} style={{ borderBottom: i < recientes.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <button
                    onClick={() => nav(`/staff/reclamos/${r.id}`)}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 active:bg-gray-50 transition-colors"
                  >
                    <span className="text-xl leading-none mt-0.5 flex-shrink-0">
                      {r.categoria_icono || '📋'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{r.titulo}</p>
                      <p className="text-xs text-gray-400 truncate">{r.ciudadano}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: (ESTADO_COLOR[r.estado] ?? '#6b7280') + '20',
                          color: ESTADO_COLOR[r.estado] ?? '#6b7280',
                        }}
                      >
                        {ESTADO_LABEL[r.estado] ?? r.estado}
                      </span>
                      <span className="text-xs text-gray-300">{timeAgo(r.creado_en)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

      </main>
    </>
  );
}
